/**
 * Client-side helpers for the keyset/offset paging contract of the datastore API
 * (see docs/architecture/datastore-datastore-design.md §7.2).
 *
 * The cursor encodes the (rowOrder, id) keyset position. OFFSET paging is
 * allowed only up to a configured cap; past the cap clients must walk
 * cursors — these helpers keep that rule in one place for the grid.
 */

export interface Cursor {
  order: number;
  id: string;
}

export function encodeCursor(c: Cursor): string {
  // btoa-safe, order first so cursors sort naturally when debugging
  return btoa(`${c.order}:${c.id}`);
}

export function decodeCursor(s: string): Cursor | null {
  try {
    const raw = atob(s);
    const idx = raw.indexOf(":");
    if (idx < 0) return null;
    const order = Number(raw.slice(0, idx));
    const id = raw.slice(idx + 1);
    if (Number.isNaN(order) || !id) return null;
    return { order, id };
  } catch {
    return null;
  }
}

/** Total pages for a table of `total` rows at `pageSize` rows per page. */
export function pageCount(total: number, pageSize: number): number {
  if (pageSize <= 0) return 0;
  return Math.ceil(total / pageSize);
}

/**
 * Whether a jump straight to `page` is allowed via OFFSET paging, or the
 * client must walk cursors instead (page is 1-based).
 */
export function isPageJumpAllowed(
  page: number,
  pageSize: number,
  maxOffset: number
): boolean {
  return (page - 1) * pageSize <= maxOffset;
}

/** Clamp a requested page to the valid [1, pageCount] range. */
export function clampPage(
  page: number,
  total: number,
  pageSize: number
): number {
  const last = Math.max(1, pageCount(total, pageSize));
  return Math.min(Math.max(1, Math.floor(page) || 1), last);
}
